import React, { Fragment, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './PlanClassement.css';
import {
  PLAN_META_LIST,
  PLAN_META_UPDATE,
  PLAN_FOLDERS,
} from '../../auth/urlParameters';

import { SimpleSpinner } from '../../../ui/Spinner';
import { ToastError, ToastConfirm } from '../../../ui/AlertesUi';

const toPlanEdit = (plan) => ({
  code: plan.code || '',
  title: plan.title || '',
  description: plan.description || '',
  version: plan.version || '1.0',
  confidentialite: plan.confidentialite || '',
  dureeConservationAnnee: plan.dureeConservationMois == null
    ? '' : Number((plan.dureeConservationMois / 12).toPrecision(15)),
  rootFolderId: plan.rootFolderId || '',
  parentId: plan.parentId || '',
  active: !!plan.active,
});

const PlanClassement = ({ embedded = false }) => {
  const session = useSelector((state) => state.sessionAuth);

  const [planTree, setPlanTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingActive, setSavingActive] = useState(false);
  const [planList, setPlanList] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [folderLoading, setFolderLoading] = useState(true);
  const [folderError, setFolderError] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());
  const [planEdit, setPlanEdit] = useState({
    code: '',
    title: '',
    description: '',
    version: '1.0',
    confidentialite: '',
    dureeConservationAnnee: '',
    rootFolderId: '',
    parentId: '',
    active: false,
  });
  const navigate = useNavigate();

  const canManage = useMemo(() => {
    const roles = session.roles || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_EDITED');
  }, [session.roles]);

  const parentOptions = useMemo(() => {
    const byParent = new Map();

    planList.forEach((plan) => {
      const parentId = plan.parentId ? String(plan.parentId) : null;
      if (!byParent.has(parentId)) byParent.set(parentId, []);
      byParent.get(parentId).push(plan);
    });

    const sortPlans = (plans) =>
      [...plans].sort((left, right) =>
        `${left.code || ''} ${left.title || ''}`.localeCompare(`${right.code || ''} ${right.title || ''}`)
      );

    const excludedIds = new Set();
    const excludeBranch = (id) => {
      excludedIds.add(id);
      (byParent.get(id) || []).forEach((child) => excludeBranch(String(child.id)));
    };

    if (selectedPlanId) {
      excludeBranch(String(selectedPlanId));
    }

    const options = [];
    const visit = (parentId, depth) => {
      sortPlans(byParent.get(parentId) || []).forEach((plan) => {
        const id = String(plan.id);
        if (!excludedIds.has(id)) {
          options.push({ plan, depth });
          visit(id, depth + 1);
        }
      });
    };

    visit(null, 0);
    return options;
  }, [planList, selectedPlanId]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const foldersByPlan = useMemo(() => {
    const groups = new Map();
    folderTree.forEach((folder) => {
      const key = String(folder.planId ?? 'unassigned');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(folder);
    });
    return groups;
  }, [folderTree]);

  const totalFolders = useMemo(() => {
    const count = (nodes) => nodes.reduce((sum, node) => sum + 1 + count(node.children || []), 0);
    return count(folderTree);
  }, [folderTree]);

  const unlinkedFolders = useMemo(() => folderTree.filter((folder) =>
    !planList.some((plan) => String(plan.id) === String(folder.planId))
  ), [folderTree, planList]);

  const fetchFolders = () => {
    setFolderLoading(true);
    setFolderError(false);
    axios.get(PLAN_FOLDERS(), authHeaders)
      .then((res) => setFolderTree(res?.data?.folders || []))
      .catch(() => { setFolderTree([]); setFolderError(true); })
      .finally(() => setFolderLoading(false));
  };

  const fetchPlanList = () => {
    setLoading(true);
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        const plans = res?.data?.plans || [];
        setPlanList(plans);
      })
      .catch(() => setPlanList([]))
      .finally(() => setLoading(false));
  };

  const changePlanActivation = (active) => {
    setSavingActive(true);
    axios.patch(PLAN_META_UPDATE(selectedPlanId), { active }, authHeaders)
      .then(() => {
        ToastConfirm(active ? 'Plan activé' : 'Plan désactivé');
        setPlanEdit((current) => ({ ...current, active }));
        fetchPlanList();
      })
      .catch((err) => ToastError(err.response?.data?.message || 'Erreur de changement de statut'))
      .finally(() => setSavingActive(false));
  };

  useEffect(() => {
    fetchPlanList();
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const current = planList.find((p) => p.id === selectedPlanId) || null;
    if (current) {
      setPlanEdit(toPlanEdit(current));
    }
    // reconstruire l'arbre plan parent/enfant
    const byId = {};
    planList.forEach((p) => {
      byId[p.id] = {
        ...p,
        reference: p.code,
        title: p.title,
        children: [],
      };
    });
    const roots = [];
    Object.values(byId).forEach((node) => {
      const parentId = node.parentId;
      if (parentId && byId[parentId]) {
        byId[parentId].children.push(node);
      } else {
        roots.push(node);
      }
    });
    setPlanTree(roots);
  }, [selectedPlanId, planList]);

  const selectPlan = (planId) => {
    setSelectedFolder(null);
    setSelectedPlanId(planId);
    setIsEditingPlan(false);
  };

  const createSubPlan = (planId) => {
    navigate(`/apps/in/plan-classement/create?parent=${encodeURIComponent(planId)}`);
  };

  const startEditingPlan = () => {
    const current = planList.find((plan) => plan.id === selectedPlanId);
    if (current) setPlanEdit(toPlanEdit(current));
    setIsEditingPlan(true);
  };

  const cancelEditingPlan = () => {
    const current = planList.find((plan) => plan.id === selectedPlanId);
    if (current) setPlanEdit(toPlanEdit(current));
    setIsEditingPlan(false);
  };

  const toggleNode = (nodeId) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const renderFolderNode = (folder) => {
    const children = folder.children || [];
    const nodeKey = `folder:${folder.id}`;
    const isCollapsed = collapsedNodes.has(nodeKey);
    const isSelected = selectedFolder?.id === folder.id;
    return (
      <li key={nodeKey}>
        <div className="classification-row">
          {children.length > 0 ? <button type="button" className="classification-toggle" aria-label={isCollapsed ? `Déplier ${folder.title}` : `Replier ${folder.title}`}
            aria-expanded={!isCollapsed} onClick={() => toggleNode(nodeKey)}><i className={`ri-arrow-${isCollapsed ? 'right' : 'down'}-s-line`} aria-hidden="true" /></button>
            : <span className="classification-toggle-spacer" />}
          <button type="button" className={`classification-item classification-folder ${children.length > 0 ? 'has-children' : ''} ${isSelected ? 'is-selected' : ''}`}
            onClick={() => { setSelectedFolder(folder); setSelectedPlanId(folder.planId); }}>
            <i className={`${children.length > 0 ? 'ri-folder-3-line' : 'ri-folder-line'} classification-item-icon`} aria-hidden="true" />
            <span className="classification-item-name"><strong>{folder.reference}</strong><span>{folder.title}</span></span>
            <span className="classification-item-kind">Dossier</span>
          </button>
        </div>
        {children.length > 0 && !isCollapsed && <ul>{children.map(renderFolderNode)}</ul>}
      </li>
    );
  };

  const renderOrgNode = (node, depth = 0) => {
    const attachedFolders = foldersByPlan.get(String(node.id)) || [];
    const hasChildren = (node.children || []).length > 0 || attachedFolders.length > 0;
    const nodeKey = `plan:${node.id}`;
    const isCollapsed = collapsedNodes.has(nodeKey);
    const isSelected = !selectedFolder && selectedPlanId === node.id;
    return (
      <li key={nodeKey}>
        <div className="classification-row">
          {hasChildren ? <button type="button" className="classification-toggle" aria-label={isCollapsed ? `Déplier ${node.title}` : `Replier ${node.title}`}
            aria-expanded={!isCollapsed} onClick={() => toggleNode(nodeKey)}><i className={`ri-arrow-${isCollapsed ? 'right' : 'down'}-s-line`} aria-hidden="true" /></button>
            : <span className="classification-toggle-spacer" />}
          <button type="button" className={`classification-item classification-plan ${depth === 0 ? 'is-root' : ''} ${isSelected ? 'is-selected' : ''}`}
            onClick={() => selectPlan(node.id)}>
            <i className="ri-archive-drawer-line classification-item-icon" aria-hidden="true" />
            <span className="classification-item-name"><strong>{node.code}</strong><span>{node.title}</span></span>
            <span className="classification-item-kind">Plan</span>
          </button>
        </div>
        {hasChildren && !isCollapsed && <ul>
          {(node.children || []).map((child) => renderOrgNode(child, depth + 1))}
          {attachedFolders.map(renderFolderNode)}
        </ul>}
      </li>
    );
  };

  return (
    <Fragment>
      {!embedded && <Breadcrumb title ="Plan de classement" />}

     <div className={embedded ? 'plan-embedded-content' : 'content'}>
        <div className={embedded ? 'container-fluid' : 'container'}>
        <div className="row">

            {!embedded && (
            <div className="col-md-3 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            )}

    <div className={embedded ? 'col-12' : 'col-md-8 col-lg-9 col-xl-9'}>
            <div className="row plan-classification-layout">
            <div className={`col-lg-${selectedPlanId || selectedFolder ? '7' : '12'} plan-layout-column`}>
            
              <div className="card plan-panel plan-org-card plan-integrated-card">
                <div className="card-body">
                  <h4 className="card-title plan-org-title d-flex align-items-center justify-content-between">
                    <span>
                      <i className="ri-git-branch-line me-2"></i> Plan de classement et dossiers
                    </span>
                    {canManage && (
                      <button
                        className="btn btn-primary btn-sm btn-uniform-action"
                        type="button"
                        onClick={() => navigate('/apps/in/plan-classement/create')}
                      >
                        <i className="ri-add-circle-line me-1"></i> Nouveau plan
                      </button>
                    )}
                  </h4>
                  <div className="classification-intro">
                    <span>{planList.length} plan{planList.length > 1 ? 's' : ''} · {totalFolders} dossier{totalFolders > 1 ? 's' : ''} · Sélectionnez un plan pour {canManage ? 'le modifier ou créer un sous-plan' : 'consulter ses détails'}.</span>
                    <span className="classification-key"><i className="ri-archive-drawer-line" aria-hidden="true" /> Plan <i className="ri-folder-3-line" aria-hidden="true" /> Dossier</span>
                  </div>
                  {!loading && planList.length === 0 && (
                    <div className="alert alert-warning">Aucun plan à afficher</div>
                  )}
                  {folderError && <div className="alert alert-warning">Les dossiers n’ont pas pu être chargés. <button type="button" className="btn btn-link p-0" onClick={fetchFolders}>Réessayer</button></div>}
                  {loading || folderLoading ? (
                    SimpleSpinner()
                  ) : (
                    <div className="classification-overview">
                      {planTree.length > 0 || unlinkedFolders.length > 0 ? (
                        <ul className="classification-tree">
                          {planTree.map((node) => renderOrgNode(node))}
                          {unlinkedFolders.length > 0 && <li className="classification-unlinked">
                            <div className="classification-unlinked-label">Dossiers sans plan de classement</div>
                            <ul>{unlinkedFolders.map(renderFolderNode)}</ul>
                          </li>}
                        </ul>
                      ) : (
                        <div className="alert alert-warning">Aucune donnée de plan à afficher</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
       
        </div>


          {(selectedPlanId || selectedFolder) && (
          <div className="col-lg-5 plan-layout-column">
              <div className="card plan-panel plan-details-card">
                <div className="card-body">
                  <h5 className="card-title d-flex align-items-center">
                    <span>
                      <i className="ri-information-line me-2"></i> {selectedFolder ? 'Détails du dossier' : 'Détails du plan'}
                    </span>
                  </h5>
                  {selectedFolder ? <div className="classification-folder-details">
                    <p className="classification-detail-type"><i className="ri-folder-3-line" aria-hidden="true" /> Dossier d’archive</p>
                    <h6>{selectedFolder.reference} — {selectedFolder.title}</h6>
                    <dl>
                      <dt>Chemin</dt><dd>{selectedFolder.path || selectedFolder.reference}</dd>
                      <dt>Plan de rattachement</dt><dd>{planList.find((plan) => String(plan.id) === String(selectedFolder.planId))?.title || 'Non renseigné'}</dd>
                      <dt>Service producteur</dt><dd>{selectedFolder.owningDepartment || selectedFolder.serviceUnit?.title || 'Non renseigné'}</dd>
                      <dt>Contenu</dt><dd>{selectedFolder.childrenCount || selectedFolder.children?.length || 0} sous-dossier(s) · {selectedFolder.documentsCount || 0} document(s)</dd>
                    </dl>
                    <button type="button" className="btn btn-primary btn-uniform-action" onClick={() => navigate(`/apps/in/dossiers-archives?folder=${selectedFolder.id}`)}>
                      Ouvrir le dossier <i className="ri-arrow-right-line" aria-hidden="true" />
                    </button>
                  </div> : canManage && isEditingPlan ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            const payload = {
                              ...planEdit,
                              dureeConservationAnnee: planEdit.dureeConservationAnnee !== ''
                                ? Number(planEdit.dureeConservationAnnee)
                                : null,
                              parentId: planEdit.parentId || null,
                            };
                            delete payload.active;
                            delete payload.rootFolderId;
                            const currentPlan = planList.find((plan) => plan.id === selectedPlanId);
                            if (currentPlan?.code === planEdit.code.trim()) delete payload.code;
                            if (currentPlan && (
                              (currentPlan.dureeConservationMois == null && planEdit.dureeConservationAnnee === '') ||
                              Math.abs(Number(planEdit.dureeConservationAnnee) * 12 - currentPlan.dureeConservationMois) < 0.000001
                            )) delete payload.dureeConservationAnnee;
                            axios
                              .patch(PLAN_META_UPDATE(selectedPlanId), payload, authHeaders)
                              .then(() => {
                                ToastConfirm('Plan mis à jour');
                                setIsEditingPlan(false);
                                fetchPlanList();
                              })
                              .catch((err) => {
                                ToastError(err.response?.data?.message || 'Erreur mise à jour plan');
                              });
                          }}
                        >
                          <div className="form-group">
                            <label>Code</label>
                            <input
                              className="form-control"
                              value={planEdit.code}
                              onChange={(e) => setPlanEdit({ ...planEdit, code: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Intitulé</label>
                            <input
                              className="form-control"
                              value={planEdit.title}
                              onChange={(e) => setPlanEdit({ ...planEdit, title: e.target.value })}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="planEditDescription">Périmètre et contenu</label>
                            <textarea id="planEditDescription" className="form-control" rows={3}
                              value={planEdit.description}
                              onChange={(e) => setPlanEdit({ ...planEdit, description: e.target.value })} />
                          </div>
                          <div className="form-group">
                            <label htmlFor="planEditVersion">Version du plan</label>
                            <input id="planEditVersion" className="form-control" value={planEdit.version}
                              onChange={(e) => setPlanEdit({ ...planEdit, version: e.target.value })}
                              required maxLength={32} />
                          </div>
                          <div className="form-group">
                            <label>Confidentialité</label>
                            <select
                              className="form-control"
                              value={planEdit.confidentialite}
                              onChange={(e) => setPlanEdit({ ...planEdit, confidentialite: e.target.value })}
                            >
                              <option value="">Choisir</option>
                              <option value="Interne">Interne</option>
                              <option value="Restreint">Restreint</option>
                              <option value="Confidentiel">Confidentiel</option>
                              <option value="Très Confidentiel">Très Confidentiel</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Durée de conservation par défaut (années)</label>
                            <input
                              className="form-control"
                              type="number"
                              min="0.01"
                              step="any"
                              value={planEdit.dureeConservationAnnee}
                              onChange={(e) =>
                                setPlanEdit({ ...planEdit, dureeConservationAnnee: e.target.value })
                              }
                            />
                            <small className="form-text text-muted">Vous pouvez saisir une fraction d’année, par exemple 1,5 an.</small>
                          </div>
                          <div className="form-group plan-parent-field">
                            <label>Parent</label>
                            <select
                              className="form-control"
                              value={planEdit.parentId}
                              onChange={(e) => setPlanEdit({ ...planEdit, parentId: e.target.value })}
                            >
                              <option value="">Aucun (racine)</option>
                              {parentOptions.map(({ plan, depth }) => (
                                <option key={plan.id} value={plan.id}>
                                  {'— '.repeat(depth)}{plan.code} — {plan.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="plan-detail-actions">
                            <button className="btn btn-primary btn-uniform-action" type="submit">
                              <i className="ri-save-line me-1" aria-hidden="true" /> Enregistrer les modifications
                            </button>
                            <button className="btn btn-outline-secondary btn-uniform-action" type="button"
                              onClick={cancelEditingPlan}>Annuler</button>
                          </div>
                          <div className="mt-3">
                            <p className="text-muted mb-2">Statut : {planEdit.active ? 'plan actif' : 'plan inactif'}. Un plan inactif reste consultable, mais ne peut plus recevoir de nouveaux dossiers ou dépôts.</p>
                            <button className="btn btn-outline-primary" type="button" disabled={savingActive}
                              onClick={() => changePlanActivation(!planEdit.active)}>
                              {savingActive ? 'Enregistrement...' : planEdit.active ? 'Désactiver ce plan' : 'Activer ce plan'}
                            </button>
                          </div>
                        </form>
                  ) : <div className="classification-folder-details">
                    <p className="classification-detail-type"><i className="ri-archive-drawer-line" aria-hidden="true" /> Plan de classement</p>
                    <h6>{planEdit.code} — {planEdit.title}</h6>
                    <dl>
                      <dt>Version</dt><dd>{planEdit.version}</dd>
                      <dt>Confidentialité</dt><dd>{planEdit.confidentialite || 'Non renseignée'}</dd>
                      <dt>Durée de conservation</dt>
                      <dd>{planEdit.dureeConservationAnnee === ''
                        ? 'Non renseignée'
                        : `${new Intl.NumberFormat('fr-FR').format(planEdit.dureeConservationAnnee)} ${Number(planEdit.dureeConservationAnnee) === 1 ? 'an' : 'ans'}`}</dd>
                      <dt>Statut</dt><dd>{planEdit.active ? 'Actif' : 'Inactif'}</dd>
                    </dl>
                    {canManage && <div className="plan-detail-actions">
                      <button type="button" className="btn btn-primary btn-uniform-action" onClick={startEditingPlan}>
                        <i className="ri-pencil-line me-1" aria-hidden="true" /> Modifier ce plan
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-uniform-action" onClick={() => createSubPlan(selectedPlanId)}>
                        <i className="ri-add-circle-line me-1" aria-hidden="true" /> Créer un sous-plan
                      </button>
                    </div>}
                  </div>}
                </div>
              </div>
            </div>
          )}


            </div></div>

          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PlanClassement;
